// Camera Raw Filter — basic RAW processing with exposure, contrast,
// highlights, shadows, whites, blacks, clarity, vibrance, saturation,
// temperature, and tint.
// Functional approximation: applies a series of tone and color adjustments
// that mimic the Camera Raw develop module.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface CameraRawParams {
  // Basic panel
  exposure: number; // -10..10, default 0
  contrast: number; // -100..100, default 0
  highlights: number; // -100..100, default 0
  shadows: number; // -100..100, default 0
  whites: number; // -100..100, default 0
  blacks: number; // -100..100, default 0
  clarity: number; // -100..100, default 0
  vibrance: number; // -100..100, default 0
  saturation: number; // -100..100, default 0
  // Color
  temperature: number; // -100..100, default 0 (blue-yellow)
  tint: number; // -100..100, default 0 (green-magenta)
  // Detail
  sharpenAmount: number; // 0..150, default 25
  sharpenRadius: number; // 0.5..3, default 1
  noiseReduction: number; // 0..100, default 0
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function applySigmoid(v: number, strength: number): number {
  // S-curve for contrast
  if (strength === 0) return v;
  const k = strength / 50;
  return 1 / (1 + Math.exp(-k * (v - 0.5) * 4));
}

export const CameraRaw: Filter<CameraRawParams> = {
  id: "camera-raw",
  displayName: "Camera Raw Filter",
  defaultParams: {
    exposure: 0,
    contrast: 0,
    highlights: 0,
    shadows: 0,
    whites: 0,
    blacks: 0,
    clarity: 0,
    vibrance: 0,
    saturation: 0,
    temperature: 0,
    tint: 0,
    sharpenAmount: 25,
    sharpenRadius: 1,
    noiseReduction: 0,
  },

  apply(
    input: PixelBuffer,
    params: CameraRawParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    if (w === 0 || h === 0) {
      dst.set(src);
      return out;
    }

    const { exposure, contrast, highlights, shadows, whites, blacks, clarity, vibrance, saturation, temperature, tint } = params;

    // Exposure factor
    const exposureFactor = Math.pow(2, exposure);

    // Temperature/tint color shifts
    const tempShift = temperature * 0.5;
    const tintShift = tint * 0.3;

    // Contrast S-curve parameters
    const contrastStrength = contrast / 50;

    // Pre-compute clarity blur (simple box blur for local contrast)
    let clarityBlur: Float32Array | null = null;
    if (clarity !== 0) {
      clarityBlur = new Float32Array(n * 3);
      const radius = Math.max(1, Math.round(Math.abs(clarity) / 20));
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          let sumR = 0, sumG = 0, sumB = 0, count = 0;
          for (let dy = -radius; dy <= radius; dy++) {
            for (let dx = -radius; dx <= radius; dx++) {
              const nx = x + dx, ny = y + dy;
              if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
                const o = (ny * w + nx) * 4;
                sumR += src[o]!;
                sumG += src[o + 1]!;
                sumB += src[o + 2]!;
                count++;
              }
            }
          }
          const idx = (y * w + x) * 3;
          clarityBlur[idx] = sumR / count;
          clarityBlur[idx + 1] = sumG / count;
          clarityBlur[idx + 2] = sumB / count;
        }
      }
    }

    // Noise reduction (simple box blur weighted by noise level)
    let nrBlur: Float32Array | null = null;
    if (params.noiseReduction > 0) {
      nrBlur = new Float32Array(n * 3);
      const radius = Math.max(1, Math.round(params.noiseReduction / 25));
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          let sumR = 0, sumG = 0, sumB = 0, count = 0;
          for (let dy = -radius; dy <= radius; dy++) {
            for (let dx = -radius; dx <= radius; dx++) {
              const nx = x + dx, ny = y + dy;
              if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
                const o = (ny * w + nx) * 4;
                sumR += src[o]!;
                sumG += src[o + 1]!;
                sumB += src[o + 2]!;
                count++;
              }
            }
          }
          const idx = (y * w + x) * 3;
          nrBlur[idx] = sumR / count;
          nrBlur[idx + 1] = sumG / count;
          nrBlur[idx + 2] = sumB / count;
        }
      }
    }

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      let r = src[o]! / 255;
      let g = src[o + 1]! / 255;
      let b = src[o + 2]! / 255;

      // 1. Exposure
      r *= exposureFactor;
      g *= exposureFactor;
      b *= exposureFactor;

      // 2. Temperature / Tint (color balance)
      r += tempShift / 255;
      g += tintShift / 255;
      b -= tempShift / 255;

      // 3. Highlights / Shadows / Whites / Blacks (tone curve)
      const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;

      // Highlights: affect bright areas
      if (highlights !== 0 && lum > 0.5) {
        const factor = (highlights / 100) * (lum - 0.5) * 2;
        r += factor * r;
        g += factor * g;
        b += factor * b;
      }

      // Shadows: affect dark areas
      if (shadows !== 0 && lum < 0.5) {
        const factor = (shadows / 100) * (0.5 - lum) * 2;
        r += factor * (1 - r);
        g += factor * (1 - g);
        b += factor * (1 - b);
      }

      // Whites: affect brightest areas
      if (whites !== 0 && lum > 0.75) {
        const factor = (whites / 100) * (lum - 0.75) * 4;
        r += factor;
        g += factor;
        b += factor;
      }

      // Blacks: affect darkest areas
      if (blacks !== 0 && lum < 0.25) {
        const factor = (blacks / 100) * (0.25 - lum) * 4;
        r -= factor;
        g -= factor;
        b -= factor;
      }

      // 4. Contrast (S-curve)
      if (contrast !== 0) {
        r = applySigmoid(clamp8(Math.round(r * 255)) / 255, contrastStrength);
        g = applySigmoid(clamp8(Math.round(g * 255)) / 255, contrastStrength);
        b = applySigmoid(clamp8(Math.round(b * 255)) / 255, contrastStrength);
      }

      // 5. Clarity (local contrast enhancement)
      if (clarity !== 0 && clarityBlur) {
        const idx = i * 3;
        const blurR = clarityBlur[idx]! / 255;
        const blurG = clarityBlur[idx + 1]! / 255;
        const blurB = clarityBlur[idx + 2]! / 255;
        const clarityFactor = clarity / 100;
        r += (r - blurR) * clarityFactor;
        g += (g - blurG) * clarityFactor;
        b += (b - blurB) * clarityFactor;
      }

      // 6. Noise reduction
      if (params.noiseReduction > 0 && nrBlur) {
        const idx = i * 3;
        const blend = params.noiseReduction / 100;
        r = r * (1 - blend) + (nrBlur[idx]! / 255) * blend;
        g = g * (1 - blend) + (nrBlur[idx + 1]! / 255) * blend;
        b = b * (1 - blend) + (nrBlur[idx + 2]! / 255) * blend;
      }

      // 7. Saturation
      if (saturation !== 0) {
        const gray = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        const satFactor = 1 + saturation / 100;
        r = gray + (r - gray) * satFactor;
        g = gray + (g - gray) * satFactor;
        b = gray + (b - gray) * satFactor;
      }

      // 8. Vibrance (smart saturation — boosts less-saturated colors more)
      if (vibrance !== 0) {
        const maxC = Math.max(r, g, b);
        const minC = Math.min(r, g, b);
        const currentSat = maxC > 0 ? (maxC - minC) / maxC : 0;
        const vibranceFactor = 1 + (vibrance / 100) * (1 - currentSat);
        const gray = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        r = gray + (r - gray) * vibranceFactor;
        g = gray + (g - gray) * vibranceFactor;
        b = gray + (b - gray) * vibranceFactor;
      }

      dst[o] = clamp8(Math.round(r * 255));
      dst[o + 1] = clamp8(Math.round(g * 255));
      dst[o + 2] = clamp8(Math.round(b * 255));
      dst[o + 3] = src[o + 3]!;
    }

    return out;
  },
};
